import { SelectedScreenType } from "@/Views/Virtual/SelectedScreensPopup/SelectedScreensPopup";

import React, { useMemo } from "react";
import {
  useSelectedAnimationScreens,
  useSelectedScreens,
} from "@/entities/selectedScreens";
import { useSelectedAppStoreScreens } from "@/entities/selectedScreens";
import { useSelectedSearchScreens } from "@/entities/selectedScreens";
import { toast } from "react-toastify";
import {
  useMultipleSavedPointsMutation,
  useMultipleSavedPointsToGroupMutation,
  useMultipleSaveStoreScreensMutation,
  useMultipleAppStoreScreensToGroupMutation,
  useMultipleSaveOnboardingScreensMutation,
  useMultipleOnboardingScreensToGroupMutation,
  useMultipleSaveAnimationScreensMutation,
  useMultipleAnimationScreensToGroupMutation,
} from "@/features/auth/api/authAPI";
import {
  MultipleSavedPointsPayload,
  MultipleSaveOnboardingScreensPayload,
  MultipleSaveStoreScreensPayload,
  MultipleSaveAnimationScreensPayload,
} from "@/features/auth/types/auth";
import { useDispatch } from "react-redux";
import { filterAppsDataActions } from "@/entities/filterAppsData";
import { useCurrentOrganization } from "@/Hooks/useCurrentOrganization";
import { useSelectedOnboardingScreens } from "@/entities/selectedScreens";
import { produce } from "immer";
import { useSelectedScreensStates } from "./useSelectedScreensStates";

interface UseSaveToGroupHandlersProps {
  type: SelectedScreenType;
  setSearchResults?: React.Dispatch<React.SetStateAction<any[]>>;
  updateScreensSavedStatus?: (
    screenIds: number[],
    isSaved: boolean,
    savedPointIds: Record<number, number> | null,
  ) => void;
  updateReplayScreensSavedStatus?: (
    screenIds: number[],
    isSaved: boolean,
    savedPointIds: Record<number, number> | null,
  ) => void;
  setAnimations?: React.Dispatch<React.SetStateAction<any[]>>;
  setSimilarScreenResults?: React.Dispatch<React.SetStateAction<any[]>>;
  setShowSavePopup: React.Dispatch<React.SetStateAction<boolean>>;
  setAppStoreScreens?: React.Dispatch<React.SetStateAction<any[]>>;
}

export const useSaveToGroupScreensHandlers = (
  props: UseSaveToGroupHandlersProps,
) => {
  const {
    type,
    setSearchResults,
    updateScreensSavedStatus,
    updateReplayScreensSavedStatus,
    setAnimations,
    setSimilarScreenResults,
    setShowSavePopup,
    setAppStoreScreens,
  } = props;
  const organization = useCurrentOrganization();
  const dispatch = useDispatch();
  const { selectedScreens, clearSelectedScreens } = useSelectedScreens();

  const {
    selectedScreens: selectedAppStoreScreens,
    clearSelectedScreens: clearSelectedAppStoreScreens,
  } = useSelectedAppStoreScreens();
  const {
    selectedScreens: selectedOnboardingScreens,
    clearSelectedScreens: clearSelectedOnboardingScreens,
  } = useSelectedOnboardingScreens();
  const {
    selectedScreens: selectedSearchScreens,
    clearSelectedScreens: clearSelectedSearchScreens,
  } = useSelectedSearchScreens();
  const {
    selectedScreens: selectedAnimationScreens,
    clearSelectedScreens: clearSelectedAnimationScreens,
  } = useSelectedAnimationScreens();

  // Get the updateScreensSavedFlag function from useSelectedScreensStates
  const { updateScreensSavedFlag, updatePointIds } = useSelectedScreensStates({
    type,
  });

  // App Replays API hooks
  const [multipleSavedPoints, { isLoading: isSavingPoints }] =
    useMultipleSavedPointsMutation();
  const [multipleSavedPointsToGroup, { isLoading: isSavingToGroup }] =
    useMultipleSavedPointsToGroupMutation();

  // App Store Screens API hooks
  const [multipleSaveStoreScreens, { isLoading: isSavingStoreScreens }] =
    useMultipleSaveStoreScreensMutation();
  const [
    multipleAppStoreScreensToGroup,
    { isLoading: isSavingStoreScreensToGroup },
  ] = useMultipleAppStoreScreensToGroupMutation();

  // Onboarding Screens API hooks
  const [
    multipleSaveOnboardingScreens,
    { isLoading: isSavingOnboardingScreens },
  ] = useMultipleSaveOnboardingScreensMutation();

  const [
    multipleOnboardingScreensToGroup,
    { isLoading: isSavingOnboardingScreensToGroup },
  ] = useMultipleOnboardingScreensToGroupMutation();

  // Animation Screens API hooks
  const [
    multipleSaveAnimationScreens,
    { isLoading: isSavingAnimationScreens },
  ] = useMultipleSaveAnimationScreensMutation();

  const [
    multipleAnimationScreensToGroup,
    { isLoading: isSavingAnimationScreensToGroup },
  ] = useMultipleAnimationScreensToGroupMutation();

  // ONBOARDING SCREENS
  const handleSaveOnboardingScreens = async () => {
    try {
      // Separate already saved screens from unsaved ones
      const savedScreens = selectedOnboardingScreens.filter(
        (screen) => screen.is_saved_pk,
      );
      const unsavedScreens = selectedOnboardingScreens.filter(
        (screen) => !screen.is_saved_pk,
      );

      // Initialize pointIds with IDs from already saved screens
      let pointIds = savedScreens.map((screen) => screen.is_saved_pk as number);

      // Only make the save points API call if there are unsaved screens
      if (unsavedScreens.length > 0) {
        // Step 1: Save the unsaved points first
        const pointsPayload: MultipleSaveOnboardingScreensPayload = {
          organizationId: organization?.uuid,
          points: unsavedScreens.map((screen) => ({
            app_ob_video: screen.app_video!,
            ts: screen.timestamp,
          })),
        };

        const savedPointsResponse =
          await multipleSaveOnboardingScreens(pointsPayload).unwrap();

        // Add newly saved point IDs to our collection
        if (
          savedPointsResponse.saved_ob_screens &&
          savedPointsResponse.saved_ob_screens.length > 0
        ) {
          const newPointIds = savedPointsResponse.saved_ob_screens.map(
            (point) => point.id,
          );
          pointIds = [...pointIds, ...newPointIds];

          // Create a mapping of screen IDs to their saved point IDs
          const savedPointIdsMap: Record<number, number> = {};
          unsavedScreens.forEach((screen, index) => {
            if (index < savedPointsResponse.saved_ob_screens.length) {
              savedPointIdsMap[screen.id] =
                savedPointsResponse.saved_ob_screens[index].id;
            }
          });

          // Update the screens' saved status in the parent component
          if (updateScreensSavedStatus) {
            updateScreensSavedStatus(
              unsavedScreens.map((screen) => screen.id),
              true,
              savedPointIdsMap,
            );
          }

          // Update the screens' saved status in Redux
          updatePointIds(savedPointIdsMap);
        }
      }

      // Mark all screens as saved in Redux
      updateScreensSavedFlag(true);

      return pointIds;
    } catch (error) {
      console.error("Failed to save screens:", error);
      toast.error("Failed to save screens");
    }
  };

  const handleSaveOnboardingScreensToGroup = async (
    pointIds: number[],
    groupId: number,
  ) => {
    try {
      // Step 2: Save all points to the group (both previously saved and newly saved)
      if (pointIds.length > 0) {
        await multipleOnboardingScreensToGroup({
          saved_group: groupId,
          saved_ob_screens: pointIds,
        }).unwrap();

        setShowSavePopup(false);

        // Clear selection and close popup after successful save
        clearSelectedOnboardingScreens();
      }
    } catch (error) {
      console.error("Failed to save screens to group:", error);
      toast.error("Failed to save screens to group");
    }
  };

  // APP REPLAYS
  const handleSaveAppReplays = async () => {
    try {
      // Separate already saved screens from unsaved ones
      const savedScreens = selectedScreens.filter(
        (screen) => screen.is_saved_pk,
      );
      const unsavedScreens = selectedScreens.filter(
        (screen) => !screen.is_saved_pk,
      );

      // Initialize pointIds with IDs from already saved screens
      let pointIds = savedScreens.map((screen) => screen.is_saved_pk as number);

      // Only make the save points API call if there are unsaved screens
      if (unsavedScreens.length > 0) {
        // Step 1: Save the unsaved points first
        const pointsPayload: MultipleSavedPointsPayload = {
          organizationId: organization?.uuid,
          points: unsavedScreens.map((screen) => ({
            app_video: screen.app_video!,
            ts: screen.ts,
          })),
        };

        const savedPointsResponse =
          await multipleSavedPoints(pointsPayload).unwrap();

        // Add newly saved point IDs to our collection
        if (
          savedPointsResponse.points &&
          savedPointsResponse.points.length > 0
        ) {
          const newPointIds = savedPointsResponse.points.map(
            (point) => point.id,
          );
          pointIds = [...pointIds, ...newPointIds];

          // Create a mapping of screen IDs to their saved point IDs for Redux update
          const savedPointIdsMap: Record<number, number> = {};
          unsavedScreens.forEach((screen, index) => {
            if (index < savedPointsResponse.points.length) {
              savedPointIdsMap[screen.id] =
                savedPointsResponse.points[index].id;
            }
          });

          // Update Redux state to mark screens as saved
          dispatch(
            filterAppsDataActions.updateScreensSavedStatus({
              screenIds: unsavedScreens.map((screen) => screen.id),
              isSaved: true,
              savedPointIds: savedPointIdsMap,
            }),
          );

          // Update similarScreenResults state if provided
          if (setSimilarScreenResults) {
            setSimilarScreenResults(
              produce((draft) => {
                unsavedScreens.forEach((screen, index) => {
                  if (index < savedPointsResponse.points.length) {
                    const screenIndex = draft.findIndex(
                      (obj) => obj.id === screen.id,
                    );
                    if (screenIndex !== -1) {
                      draft[screenIndex].is_saved = true;
                      draft[screenIndex].is_saved_pk =
                        savedPointsResponse.points[index].id;
                    }
                  }
                });
              }),
            );
          }

          // Update the screens' saved status
          if (updateReplayScreensSavedStatus) {
            updateReplayScreensSavedStatus(
              unsavedScreens.map((screen) => screen.id),
              true,
              savedPointIdsMap,
            );
          }
          updatePointIds(savedPointIdsMap);
        }
      }
      updateScreensSavedFlag(true);

      return pointIds;
    } catch (error) {
      console.error("Failed to save screens:", error);
      toast.error("Failed to save screens");
    }
  };

  const handleSaveAppReplaysToGroup = async (
    pointIds: number[],
    groupId: number,
  ) => {
    try {
      // Step 2: Save all points to the group (both previously saved and newly saved)
      if (pointIds.length > 0) {
        await multipleSavedPointsToGroup({
          saved_group: groupId,
          saved_points: pointIds,
        }).unwrap();

        setShowSavePopup(false);

        // Clear selection and close popup after successful save
        clearSelectedScreens();
      }
    } catch (error) {
      console.error("Failed to save screens to group:", error);
      toast.error("Failed to save screens to group");
    }
  };

  // ANIMATION SCREENS
  const handleSaveAnimationScreens = async () => {
    try {
      // Separate already saved screens from unsaved ones
      const savedScreens = selectedAnimationScreens.filter(
        (screen) => screen.is_saved,
      );
      const unsavedScreens = selectedAnimationScreens.filter(
        (screen) => !screen.is_saved,
      );

      // Initialize screenIds with IDs from already saved screens
      let screenIds = savedScreens.map((screen) => screen.is_saved_pk);

      // Only make the save screens API call if there are unsaved screens
      if (unsavedScreens.length > 0) {
        // Step 1: Save the unsaved screens first
        const screensPayload: MultipleSaveAnimationScreensPayload = {
          organizationId: organization?.uuid,
          app_video_animations: unsavedScreens.map((screen) => screen.id),
        };

        const savedScreensResponse =
          await multipleSaveAnimationScreens(screensPayload).unwrap();

        // Add newly saved screen IDs to our collection
        if (
          savedScreensResponse.saved_animations &&
          savedScreensResponse.saved_animations.length > 0
        ) {
          const newScreenIds = savedScreensResponse.saved_animations.map(
            (screen) => screen.id,
          );
          screenIds = [...screenIds, ...newScreenIds];

          // Create a mapping of animation IDs to their saved IDs
          const savedAnimationIdsMap = {};
          unsavedScreens.forEach((screen, index) => {
            if (index < savedScreensResponse.saved_animations.length) {
              savedAnimationIdsMap[screen.id] =
                savedScreensResponse.saved_animations[index].id;
            }
          });

          // Update animations state if provided
          if (setAnimations) {
            setAnimations((prevAnimations) =>
              prevAnimations.map((animation) => {
                if (
                  unsavedScreens.some((screen) => screen.id === animation.id)
                ) {
                  return {
                    ...animation,
                    is_saved: true,
                    is_saved_pk: savedAnimationIdsMap[animation.id] || null,
                  };
                }
                return animation;
              }),
            );
          }
          updatePointIds(savedAnimationIdsMap);
        }
      }
      updateScreensSavedFlag(true);

      return screenIds;
    } catch (error) {
      console.error("Failed to save animation screens to group:", error);
      toast.error("Failed to save screens to group");
    }
  };

  const handleSaveAnimationScreensToGroup = async (
    screenIds: number[],
    groupId: number,
  ) => {
    try {
      // Step 2: Save all screens to the group (both previously saved and newly saved)
      if (screenIds.length > 0) {
        await multipleAnimationScreensToGroup({
          saved_group: groupId,
          saved_animations: screenIds,
        }).unwrap();

        setShowSavePopup(false);

        // Clear selection and close popup after successful save
        clearSelectedAnimationScreens();
      }
    } catch (error) {
      console.error("Failed to save animation screens to group:", error);
      toast.error("Failed to save screens to group");
    }
  };

  // APP STORE SCREENS
  const handleSaveAppStoreScreens = async () => {
    try {
      // Separate already saved screens from unsaved ones
      const savedScreens = selectedAppStoreScreens.filter(
        (screen) => screen.is_saved,
      );
      const unsavedScreens = selectedAppStoreScreens.filter(
        (screen) => !screen.is_saved,
      );

      // Initialize screenIds with IDs from already saved screens
      let screenIds = savedScreens.map((screen) => screen.is_saved_pk);

      // Only make the save screens API call if there are unsaved screens
      if (unsavedScreens.length > 0) {
        // Step 1: Save the unsaved screens first
        const screensPayload: MultipleSaveStoreScreensPayload = {
          organizationId: organization?.uuid,
          store_screens: unsavedScreens.map((screen) => screen.id),
        };

        const savedScreensResponse =
          await multipleSaveStoreScreens(screensPayload).unwrap();

        // Add newly saved screen IDs to our collection
        if (
          savedScreensResponse.saved_store_screens &&
          savedScreensResponse.saved_store_screens.length > 0
        ) {
          const newScreenIds = savedScreensResponse.saved_store_screens.map(
            (screen) => screen.id,
          );
          screenIds = [...screenIds, ...newScreenIds];

          // Create a mapping of screen IDs to their saved screen IDs for Redux update
          const savedScreenIdsMap: Record<number, number> = {};
          unsavedScreens.forEach((screen, index) => {
            if (index < savedScreensResponse.saved_store_screens.length) {
              savedScreenIdsMap[screen.id] =
                savedScreensResponse.saved_store_screens[index].id;
            }
          });

          // Update the appStoreScreens state if provided
          if (setAppStoreScreens) {
            setAppStoreScreens(
              produce((draft) => {
                unsavedScreens.forEach((screen, index) => {
                  if (index < savedScreensResponse.saved_store_screens.length) {
                    const screenIndex = draft.findIndex(
                      (obj) => obj.id === screen.id,
                    );
                    if (screenIndex !== -1) {
                      draft[screenIndex].is_saved = true;
                      draft[screenIndex].is_saved_pk =
                        savedScreensResponse.saved_store_screens[index].id;
                    }
                  }
                });
              }),
            );
          }

          // Update Redux state to mark screens as saved
          dispatch(
            filterAppsDataActions.updateAppStoreScreensSavedStatus({
              screenIds: unsavedScreens.map((screen) => screen.id),
              isSaved: true,
              savedPointIds: savedScreenIdsMap,
            }),
          );

          // Update the screens' saved status in the parent component
          if (updateScreensSavedStatus) {
            updateScreensSavedStatus(
              unsavedScreens.map((screen) => screen.id),
              true,
              savedScreenIdsMap,
            );
          }
          updatePointIds(savedScreenIdsMap);
        }
      }
      updateScreensSavedFlag(true);

      return screenIds;
    } catch (error) {
      console.error("Failed to save app store screens to group:", error);
      toast.error("Failed to save screens to group");
    }
  };

  const handleSaveAppStoreScreensToGroup = async (
    screenIds: number[],
    groupId: number,
  ) => {
    try {
      // Step 2: Save all screens to the group (both previously saved and newly saved)
      if (screenIds.length > 0) {
        await multipleAppStoreScreensToGroup({
          saved_group: groupId,
          saved_store_screens: screenIds,
        }).unwrap();

        setShowSavePopup(false);

        // Clear selection and close popup after successful save
        clearSelectedAppStoreScreens();
      }
    } catch (error) {
      console.error("Failed to save app store screens to group:", error);
      toast.error("Failed to save screens to group");
    }
  };

  // SEARCH SCREENS
  const handleSaveSearchScreens = async () => {
    try {
      // Separate already saved screens from unsaved ones
      const savedScreens = selectedSearchScreens.filter(
        (screen) => screen.is_saved,
      );
      const unsavedScreens = selectedSearchScreens.filter(
        (screen) => !screen.is_saved,
      );

      // Initialize screenIds with IDs from already saved screens
      let screenIds = savedScreens.map((screen) => screen.is_saved_pk);

      // Only make the save screens API call if there are unsaved screens
      if (unsavedScreens.length > 0) {
        // Step 1: Save the unsaved screens first
        const screensPayload: MultipleSavedPointsPayload = {
          organizationId: organization?.uuid,
          points: unsavedScreens.map((screen) => ({
            app_video: screen.app_video_id!,
            ts: screen.timestamp,
          })),
        };

        // Define savedScreensResponse properly
        const savedScreensResponse =
          await multipleSavedPoints(screensPayload).unwrap();

        // Add newly saved screen IDs to our collection
        if (
          savedScreensResponse.points &&
          savedScreensResponse.points.length > 0
        ) {
          const newScreenIds = savedScreensResponse.points.map(
            (screen) => screen.id,
          );
          screenIds = [...screenIds, ...newScreenIds];

          // Create a mapping of screen IDs to their saved point IDs
          const savedPointIdsMap: Record<number, number> = {};
          unsavedScreens.forEach((screen, index) => {
            if (index < savedScreensResponse.points.length) {
              savedPointIdsMap[screen.id] =
                savedScreensResponse.points[index].id;
            }
          });

          // Update the searchResults state if the prop is provided
          if (setSearchResults) {
            setSearchResults(
              produce((draft) => {
                unsavedScreens.forEach((screen, index) => {
                  if (index < savedScreensResponse.points.length) {
                    const screenIndex = draft.findIndex(
                      (obj) => obj.id === screen.id,
                    );
                    if (screenIndex !== -1) {
                      draft[screenIndex].is_saved = true;
                      draft[screenIndex].is_saved_pk =
                        savedScreensResponse.points[index].id;
                    }
                  }
                });
              }),
            );
          }

          // Update Redux state with the saved point IDs
          updatePointIds(savedPointIdsMap);
        }
      }

      // Mark all screens as saved in Redux
      updateScreensSavedFlag(true);

      return screenIds;
    } catch (error) {
      console.error("Failed to save search screens to group:", error);
      toast.error("Failed to save screens to group");
    }
  };

  const handleSaveSearchScreensToGroup = async (
    screenIds: number[],
    groupId: number,
  ) => {
    try {
      // Step 2: Save all screens to the group (both previously saved and newly saved)
      if (screenIds.length > 0) {
        await multipleSavedPointsToGroup({
          saved_group: groupId,
          saved_points: screenIds,
        }).unwrap();

        setShowSavePopup(false);

        // Clear selection and close popup after successful save
        clearSelectedSearchScreens();
      }
    } catch (error) {
      console.error("Failed to save search screens to group:", error);
      toast.error("Failed to save screens to group");
    }
  };

  const handleSaveToGroup = (pointIds: number[], groupId: number) => {
    switch (type) {
      case "appReplays":
        return handleSaveAppReplaysToGroup(pointIds, groupId);
      case "onboardingScreens":
        return handleSaveOnboardingScreensToGroup(pointIds, groupId);
      case "animationScreens":
        return handleSaveAnimationScreensToGroup(pointIds, groupId);
      case "appStoreScreens":
        return handleSaveAppStoreScreensToGroup(pointIds, groupId);
      case "searchScreens":
        return handleSaveSearchScreensToGroup(pointIds, groupId);
      default:
        return;
    }
  };

  const handleSave = () => {
    switch (type) {
      case "onboardingScreens":
        return handleSaveOnboardingScreens();
      case "appReplays":
        return handleSaveAppReplays();
      case "animationScreens":
        return handleSaveAnimationScreens();
      case "appStoreScreens":
        return handleSaveAppStoreScreens();
      case "searchScreens":
        return handleSaveSearchScreens();
      default:
        return;
    }
  };

  const isSaveToGroupLoading = useMemo(() => {
    switch (type) {
      case "appReplays":
        return isSavingPoints || isSavingToGroup;
      case "appStoreScreens":
        return isSavingStoreScreens || isSavingStoreScreensToGroup;
      case "searchScreens":
        return isSavingPoints || isSavingToGroup;
      case "onboardingScreens":
        return isSavingOnboardingScreens || isSavingOnboardingScreensToGroup;
      case "animationScreens":
        return isSavingAnimationScreens || isSavingAnimationScreensToGroup;

      default:
        return false;
    }
  }, [
    type,
    isSavingPoints,
    isSavingStoreScreens,
    isSavingOnboardingScreens,
    isSavingAnimationScreens,
  ]);

  return {
    handleSave,
    handleSaveToGroup,
    isSaveToGroupLoading,
  };
};
